import React from "react";

/**
 * Turns a thrown error into something a user can act on.
 *
 * The raw text is often a developer diagnostic — the dev proxy, for example,
 * reports "Cannot reach the API backend (proxy error). Target:
 * http://0.0.0.0:8000 Reason: ECONNREFUSED Fix: start your API on that
 * host/port, or set REACT_APP_API_BASE_URL…". That is useful while building
 * and meaningless (and alarming) to the person using the app, so the raw text
 * moves behind a disclosure and only in development.
 */
function humanizeError(raw: string): string {
  const text = (raw || "").toLowerCase();
  if (/proxy error|econnrefused|failed to fetch|networkerror|err_connection/.test(text)) {
    return "We couldn't reach the server. Check your connection and try again.";
  }
  if (/timeout|timed out|etimedout/.test(text)) {
    return "The server took too long to respond. Try again in a moment.";
  }
  if (/(401|unauthorized)/.test(text)) {
    return "Your session has expired. Sign in again to continue.";
  }
  if (/(403|forbidden)/.test(text)) {
    return "You don't have access to this.";
  }
  if (/(404|not found)/.test(text)) {
    return "We couldn't find what you were looking for.";
  }
  if (/(5\d\d)|server error|internal/.test(text)) {
    return "Something went wrong on our end. Try again shortly.";
  }
  return raw;
}

export function QueryErrorBanner({
  title,
  message,
  onRetry,
  retrying,
}: {
  title?: string;
  message: string;
  onRetry: () => void;
  retrying?: boolean;
}): React.ReactElement {
  const friendly = humanizeError(message);
  const hasDetail = friendly !== message && process.env.NODE_ENV === "development";

  return (
    <div className="app-shell-query-error" role="alert">
      <div className="app-shell-query-error-text">
        <div className="app-shell-query-error-title">{title ?? "Could not load data"}</div>
        <p className="app-shell-query-error-msg">{friendly}</p>
        {hasDetail ? (
          <details className="app-shell-query-error-detail">
            <summary>Technical details</summary>
            <p>{message}</p>
          </details>
        ) : null}
      </div>
      <button type="button" className="eventos-nav-btn" onClick={() => onRetry()} disabled={retrying}>
        {retrying ? "Retrying…" : "Retry"}
      </button>
    </div>
  );
}

export function KpiStripSkeleton({ count = 4 }: { count?: number }): React.ReactElement {
  return (
    <div className="app-shell-kpi-strip" aria-hidden>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="app-shell-kpi app-shell-skeleton-block">
          <div className="app-shell-skeleton-line app-shell-skeleton-line--sm" />
          <div className="app-shell-skeleton-line app-shell-skeleton-line--lg" />
          <div className="app-shell-skeleton-line app-shell-skeleton-line--md" />
        </div>
      ))}
    </div>
  );
}

export function TableSkeletonRows({ rows = 6, cols = 6 }: { rows?: number; cols?: number }): React.ReactElement {
  return (
    <>
      {Array.from({ length: rows }).map((_, ri) => (
        <tr key={ri}>
          {Array.from({ length: cols }).map((_, ci) => (
            <td key={ci}>
              <div className="app-shell-skeleton-line app-shell-skeleton-line--table" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/**
 * Loading placeholder for card lists (Events, Artists, Venues, Contacts).
 *
 * Those screens render `<article>` cards, not table rows. They were using
 * TableSkeletonRows, which emits `<tr>`/`<td>` — invalid inside a `<div>`
 * ("validateDOMNesting: <div> cannot contain a nested <tr>") and shaped
 * nothing like the content it stands in for. A skeleton should match the
 * layout it precedes, so the page doesn't jump when data lands.
 */
export function ListSkeletonCards({ rows = 6 }: { rows?: number }): React.ReactElement {
  return (
    <div className="app-shell-list-skeleton" aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="app-shell-list-skeleton-card app-shell-skeleton-block">
          <div className="app-shell-skeleton-line app-shell-skeleton-line--md" />
          <div className="app-shell-skeleton-line app-shell-skeleton-line--sm" />
        </div>
      ))}
    </div>
  );
}
